/**
 * Clouds — camada de nuvens suaves (blur + baixa opacidade) que pairam sobre a
 * floresta ao redor da cidade e derivam lentamente, dando dinamismo à cena.
 * As nuvens SOMEM sobre o miolo da cidade (fade), mantendo-a como elemento
 * principal na tela.
 *
 * UMA CHAMADA DE DESENHO. Antes eram 18 `Mesh` independentes, cada um com o SEU
 * material (porque a opacidade do fade é por nuvem, e opacidade vive no material)
 * e a SUA geometria (porque cada nuvem tem um tamanho). Isso dava 18 draw calls e
 * 18 materiais — e draw call é o que derruba o fps numa GPU integrada. Agora é um
 * único InstancedMesh: geometria e material compartilhados, e o que varia por
 * nuvem (tamanho, rotação, posição e opacidade do fade) viaja em atributos de
 * instância. O fade é calculado NO SHADER a partir da posição da instância, então
 * a CPU não precisa mexer em opacidade de material nenhum.
 *
 * DERIVA A 10 Hz, INTERPOLADA NO SHADER. Mover 18 nuvens é barato, mas não há por
 * que refazer a conta 60 vezes por segundo para um deslocamento de ~1 unidade por
 * segundo. A posição é recalculada 10×/s e o vértice avança sozinho entre as
 * atualizações (`uDrift`), então o movimento continua contínuo — sem passos.
 */
import * as THREE from 'three';

const N = 18;
const BOUND = 330;
const UPDATE_HZ = 10;
const UPDATE_DT = 1 / UPDATE_HZ;
// Fade: a nuvem some sobre a cidade e aparece na borda da floresta. Mesmos valores
// de antes, agora avaliados no vertex shader.
const FADE_X = 138, FADE_Z = 90, FADE_W = 0.5;

export function createClouds() {
  const group = new THREE.Group();
  group.name = 'clouds';

  // quad unitário deitado (a escala por instância dá o tamanho de cada nuvem)
  const geo = new THREE.InstancedBufferGeometry();
  const base = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  geo.index = base.index;
  geo.setAttribute('position', base.attributes.position);
  geo.setAttribute('uv', base.attributes.uv);

  const iPos = new THREE.InstancedBufferAttribute(new Float32Array(N * 3), 3);
  const iSize = new THREE.InstancedBufferAttribute(new Float32Array(N * 2), 2); // largura, profundidade
  const iRot = new THREE.InstancedBufferAttribute(new Float32Array(N), 1);      // giro em Y
  const iBase = new THREE.InstancedBufferAttribute(new Float32Array(N), 1);     // opacidade máxima
  const iVel = new THREE.InstancedBufferAttribute(new Float32Array(N * 2), 2);  // deriva (u/s)
  iPos.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('iPos', iPos);
  geo.setAttribute('iSize', iSize);
  geo.setAttribute('iRot', iRot);
  geo.setAttribute('iBase', iBase);
  geo.setAttribute('iVel', iVel);
  geo.instanceCount = N;

  const clouds = [];
  for (let i = 0; i < N; i++) {
    const w = 60 + Math.random() * 95;
    const d = w * (0.6 + Math.random() * 0.35);
    const x = (Math.random() * 2 - 1) * BOUND;
    const y = 16 + Math.random() * 18;
    const z = (Math.random() * 2 - 1) * 250;
    iPos.array[i * 3] = x; iPos.array[i * 3 + 1] = y; iPos.array[i * 3 + 2] = z;
    iSize.array[i * 2] = w; iSize.array[i * 2 + 1] = d;
    iRot.array[i] = Math.random() * Math.PI;
    iBase.array[i] = 0.24 + Math.random() * 0.26;
    const vx = 0.8 + Math.random() * 1.5;
    const vz = (Math.random() - 0.5) * 0.5;
    iVel.array[i * 2] = vx; iVel.array[i * 2 + 1] = vz;
    clouds.push({ x, y, z, vx, vz });
  }

  const mat = new THREE.MeshBasicMaterial({
    map: makeCloudTexture(),
    color: '#c2d8c9',
    transparent: true,
    depthWrite: false,
    fog: false,      // a nuvem já tem o seu próprio fade; o fog só a apagaria 2×
    toneMapped: true // como antes: entra no tone mapping junto com a cena
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uDrift = { value: 0 }; // segundos desde a última atualização
    mat.userData.shader = shader;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
      attribute vec3 iPos;
      attribute vec2 iSize;
      attribute float iRot;
      attribute float iBase;
      attribute vec2 iVel;
      uniform float uDrift;
      varying float vFade;`)
      .replace('#include <project_vertex>', `
      // gira o quad no plano do chão e aplica o tamanho da instância
      float cs = cos(iRot), sn = sin(iRot);
      vec2 p = vec2(position.x * iSize.x, position.z * iSize.y);
      vec3 local = vec3(p.x * cs - p.y * sn, 0.0, p.x * sn + p.y * cs);
      // a CPU atualiza a posição a 10 Hz; entre uma atualização e outra a nuvem
      // continua derivando aqui, COM A VELOCIDADE DELA (por isso iVel é atributo:
      // um uniform único não conseguiria interpolar N velocidades diferentes).
      // No instante da atualização a CPU soma vel*passo em iPos e zera uDrift, então
      // a posição renderizada não dá salto.
      vec3 centro = vec3(iPos.x + uDrift * iVel.x, iPos.y, iPos.z + uDrift * iVel.y);
      vec3 wp = centro + local;
      // FADE a partir do CENTRO da nuvem, não do vértice: assim a nuvem inteira
      // esmaece junto, exatamente como quando cada uma tinha o seu material.
      float dd = max(abs(centro.x) / ${FADE_X.toFixed(1)}, abs(centro.z) / ${FADE_Z.toFixed(1)});
      vFade = iBase * clamp((dd - 1.0) / ${FADE_W.toFixed(1)}, 0.0, 1.0);
      vec4 mvPosition = modelViewMatrix * vec4(wp, 1.0);
      gl_Position = projectionMatrix * mvPosition;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n      varying float vFade;')
      // a opacidade vira POR INSTANCIA (antes era `opacity` do material, e era por
      // isso que cada nuvem precisava do seu próprio material)
      .replace('#include <dithering_fragment>', `
      gl_FragColor.a *= vFade;
      #include <dithering_fragment>`);
  };

  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'cloudsInstanced';
  mesh.renderOrder = 3;
  mesh.frustumCulled = false;   // a caixa da geometria é só o quad unitário
  mesh.matrixAutoUpdate = false; // fica na origem; a posição é por instância
  group.add(mesh);

  group.userData = { clouds, mesh, iPos, mat, acc: 0, drift: 0 };
  return group;
}

/**
 * Deriva lenta. Chamada a cada frame, mas só recalcula posições a UPDATE_HZ — o
 * shader cobre o intervalo com `uDrift`, então o movimento segue contínuo.
 * Não faz nada com o grupo invisível (a chave nas Configurações).
 */
export function updateClouds(group, dt) {
  if (!group.visible) return;
  const u = group.userData;
  u.acc += dt;
  u.drift += dt;
  const shader = u.mat.userData.shader;
  if (shader) shader.uniforms.uDrift.value = u.drift;
  if (u.acc < UPDATE_DT) return;

  const step = u.acc;
  u.acc = 0;
  u.drift = 0; // a posição real absorveu a deriva acumulada
  const arr = u.iPos.array;
  for (let i = 0; i < u.clouds.length; i++) {
    const c = u.clouds[i];
    c.x += c.vx * step;
    c.z += c.vz * step;
    if (c.x > BOUND) c.x -= BOUND * 2;
    if (c.z > 260) c.z -= 520;
    else if (c.z < -260) c.z += 520;
    arr[i * 3] = c.x;
    arr[i * 3 + 2] = c.z;
  }
  u.iPos.needsUpdate = true;
}

/** Textura de nuvem: vários blobs radiais suaves (aspecto fofo e borrado). */
function makeCloudTexture() {
  // 128 em vez de 256: a nuvem é uma mancha borrada de baixa frequência exibida a
  // ~1000 px de largura na tela — a textura é esticada de qualquer jeito, e metade
  // da resolução não muda a imagem. Corta 4× a memória e a banda de amostragem.
  const s = 128;
  const cv = document.createElement('canvas');
  cv.width = cv.height = s;
  const ctx = cv.getContext('2d');
  const blob = (x, y, r, a) => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, s, s);
  };
  for (let i = 0; i < 9; i++) {
    blob(
      s * (0.28 + Math.random() * 0.44),
      s * (0.34 + Math.random() * 0.32),
      s * (0.14 + Math.random() * 0.16),
      0.42
    );
  }
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
